import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import { useApiClient } from "../../contexts/ApiClientContext";
import PagedData from "../../model/PagedData";
import APIEndPoints from "../../utils/APIEndPoints";
import Loading from "../common/Loading";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import UserRoles from "../../model/enums/UserRole";
import _3DInstance from "../../model/_3DInstance";
import _3dModelsListElement from "./_3dInstancesListElement";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";

/*
  Component to manage the display of the existing SITES list
*/
function _3dModelsList() {
  // read the ID from the url
  const { id } = useParams();

  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the navigator
  const navigate = useNavigate();
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // activities information to be displayed
  const [data, setData] = useState<_3DInstance[]>([]);
  const [showMore, setShowMore] = useState(false);
  // manage data loading animation
  const [loading, setLoading] = useState(true);

  // load record data from the server
  useEffect(() => {
    apiClient
      ?.get<PagedData<_3DInstance[]>>({
        url: APIEndPoints._3DINSTANCES.PAGED(1, 5, parseInt(id!)),
        requiresToken: true,
      })
      .then((response: PagedData<_3DInstance[]>) => {
        setData(response.items.slice(0, 4));
        setShowMore(response.items.length > 4);
      })
      .catch(() => {
        navigate(SETTINGS.get_path({ section: AppSections.SIGN_IN }));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [apiClient, id, navigate]);

  const handleShowMore = () => {
    apiClient
      ?.get<PagedData<_3DInstance[]>>({
        url: APIEndPoints._3DINSTANCES.ALL(parseInt(id!)),
        requiresToken: true,
      })
      .then((response: PagedData<_3DInstance[]>) => {
        setData(response.items);
        setShowMore(false);
      })
      .catch(() => {
        navigate(SETTINGS.get_path({ section: AppSections.SIGN_IN }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="details-grid">
      {data?.length > 0 &&
        data?.map((item) => {
          const canEdit =
            (item.id_user === authManager.getUserID() ||
              authManager.getUserRole() === UserRoles.ADMIN) &&
            !EmbeddingToolInfo.isChemiTool();
          return (
            <_3dModelsListElement
              data={item}
              key={item.id}
              canEdit={canEdit}
              navigable={true}
            />
          );
        })}

      {showMore && (
        <div className="details-show-more">
          <Button
            label="Show all 3D models"
            onClick={handleShowMore}
            purpose={ButtonAction.UPDATE}
          />
        </div>
      )}

      {data?.length === 0 && (
        <div className="details-no-records">No 3D models available.</div>
      )}
    </div>
  );
}

export default _3dModelsList;
